// Export-template registry (statutory submissions). Operators author the
// DESTINATION upload formats (NSSF/WCF schedules, per-bank salary files) as
// versioned, effective-dated rows; a PUBLISHED row overrides the disk
// country-pack YAML at generation time. This is where outreach answers land:
// WCF's confirmed vocabulary or CRDB's beneficiary-bank list become a new
// template version here — no deploy. Maker-checker: the draft's author cannot
// publish it.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Drawer, Form, Input, Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '../../components/console';
import { useAppSelector } from '../../app/hooks';
import { selectRoles } from '../../features/auth/authSlice';
import {
  listExportTemplates, createExportTemplateDraft, updateExportTemplateDraft,
  publishExportTemplate, retireExportTemplate,
  type ExportTemplateRow, type DiskDefault,
} from '../../services/exportTemplateService';

const { Text, Paragraph } = Typography;

const AUTHOR_ROLES = ['tax_officer', 'platform_admin', 'platform_owner', 'super_admin', 'system_admin'];
const PUBLISH_ROLES = ['platform_admin', 'platform_owner', 'super_admin', 'system_admin'];

const EXPORT_TYPES = [
  'bank_payment', 'nssf_statutory', 'wcf_statutory', 'tra_statutory', 'sdl_statutory', 'heslb_statutory',
];

const STATUS_COLOR: Record<string, string> = { draft: 'gold', published: 'green', retired: 'default' };

// A minimal skeleton so authors start from the real spec shape rather than a
// blank box. Same schema the YAML engine parses (docs/payroll-statutory-submissions).
const SPEC_SKELETON = {
  export_type: 'wcf_statutory',
  name: 'WCF Return',
  version: '2026.2',
  supported_formats: ['csv', 'xlsx'],
  default_format: 'xlsx',
  scope: 'per_employee',
  sheet_name: 'WCF',
  file_name: 'WCF_RETURN_{MONTH}_{YEAR}',
  totals_row: false,
  mappings: { gender: { male: 'MALE', female: 'FEMALE' } },
  columns: [{ field: 'wcf_no', header: 'wcf_number', required: true }],
  validation_rules: [{ rule: 'required', field: 'wcf_no', message: 'WCF number is missing' }],
  formatter_rules: { money: { decimals: 2 } },
};

const ExportTemplatesPage: React.FC = () => {
  const roles = useAppSelector(selectRoles);
  const canAuthor = roles.some((r) => AUTHOR_ROLES.includes(r));
  const canPublish = roles.some((r) => PUBLISH_ROLES.includes(r));

  const [rows, setRows] = useState<ExportTemplateRow[]>([]);
  const [disk, setDisk] = useState<DiskDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<ExportTemplateRow | 'new' | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listExportTemplates();
      setRows(res.templates);
      setDisk(res.disk_defaults);
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Could not load export templates');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const openEditor = (row: ExportTemplateRow | 'new') => {
    setEditing(row);
    if (row === 'new') {
      form.setFieldsValue({
        country_code: 'TZ', export_type: 'wcf_statutory', bank_code: '', template_version: '',
        effective_from: '', spec: JSON.stringify(SPEC_SKELETON, null, 2),
      });
    } else {
      form.setFieldsValue({
        country_code: row.country_code, export_type: row.export_type, bank_code: row.bank_code ?? '',
        template_version: row.template_version, effective_from: row.effective_from,
        spec: JSON.stringify(row.spec, null, 2),
      });
    }
  };

  const save = async () => {
    const v = await form.validateFields();
    let spec: unknown;
    try {
      spec = JSON.parse(v.spec);
    } catch {
      message.error('The spec must be valid JSON');
      return;
    }
    setBusy('save');
    try {
      if (editing === 'new') {
        await createExportTemplateDraft({
          country_code: v.country_code, export_type: v.export_type, bank_code: v.bank_code || undefined,
          template_version: v.template_version, effective_from: v.effective_from || undefined, spec,
        });
        message.success('Draft created');
      } else if (editing) {
        await updateExportTemplateDraft(editing.id, {
          template_version: v.template_version, effective_from: v.effective_from || undefined, spec,
        });
        message.success('Draft updated');
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Save failed');
    } finally {
      setBusy(null);
    }
  };

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await fn();
      message.success(label === 'publish' ? 'Published — this version now drives generation.' : 'Retired.');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const columns: ColumnsType<ExportTemplateRow> = [
    { title: 'Country', dataIndex: 'country_code', width: 90 },
    { title: 'Export type', dataIndex: 'export_type', width: 160, render: (t) => <Text code>{t}</Text> },
    { title: 'Bank', dataIndex: 'bank_code', width: 90, render: (b) => b || '—' },
    { title: 'Version', dataIndex: 'template_version', width: 100 },
    { title: 'Tenant', dataIndex: 'tenant_id', width: 120, render: (t) => (t ? <Text code>{String(t).slice(0, 8)}…</Text> : 'platform') },
    { title: 'Effective from', dataIndex: 'effective_from', width: 130 },
    { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
    {
      title: 'Actions',
      width: 260,
      render: (_, row) => (
        <Space>
          {row.status === 'draft' && canAuthor && (
            <Button size="small" onClick={() => openEditor(row)}>Edit</Button>
          )}
          {row.status === 'draft' && canPublish && (
            <Button size="small" type="primary" loading={busy === 'publish'} onClick={() => act('publish', () => publishExportTemplate(row.id))}>
              Publish
            </Button>
          )}
          {row.status !== 'retired' && canPublish && (
            <Button size="small" danger loading={busy === 'retire'} onClick={() =>
              Modal.confirm({
                title: 'Retire this template version?',
                content: 'Generation stops matching it; earlier artifacts keep their recorded version.',
                onOk: () => act('retire', () => retireExportTemplate(row.id)),
              })
            }>
              Retire
            </Button>
          )}
          {row.status !== 'draft' && (
            <Button size="small" onClick={() => openEditor(row)} disabled={row.status !== 'draft' && !canAuthor}>
              View
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const diskColumns: ColumnsType<DiskDefault> = [
    { title: 'Country', dataIndex: 'country_code', width: 90 },
    { title: 'Export type', dataIndex: 'export_type', width: 160, render: (t) => <Text code>{t}</Text> },
    { title: 'Bank', dataIndex: 'bank_code', width: 90, render: (b) => b || '—' },
    { title: 'Version', dataIndex: 'template_version', width: 100, render: (v) => v || '—' },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Source', width: 110, render: () => <Tag>disk baseline</Tag> },
  ];

  const isViewOnly = editing !== 'new' && editing !== null && editing.status !== 'draft';

  return (
    <div>
      <PageHeader
        title="Export templates"
        subtitle="Destination upload formats (NSSF / WCF / per-bank salary files). A published row overrides the disk baseline at generation time — outreach-confirmed vocabularies and new banks land here as config, no deploy."
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Refresh</Button>
            {canAuthor && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor('new')}>New draft</Button>
            )}
          </Space>
        }
      />

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Fail-closed vocabularies"
        description="Controlled mappings (WCF gender/employment category, CRDB beneficiary banks) must contain only values VERIFIED with the destination. An unmapped value blocks generation naming the employee — never add a guessed equivalent."
      />

      <Table rowKey="id" size="middle" loading={loading} columns={columns} dataSource={rows}
        locale={{ emptyText: 'No registry rows yet — the disk baseline below applies.' }} pagination={false} />

      <PageHeader title="Disk baseline" subtitle="Templates shipped in the country pack — apply wherever no published registry row overrides them." />
      <Table rowKey={(d) => `${d.country_code}:${d.export_type}:${d.bank_code ?? ''}`} size="small"
        columns={diskColumns} dataSource={disk} pagination={false} />

      <Drawer
        title={editing === 'new' ? 'New template draft' : isViewOnly ? 'Template (read-only)' : 'Edit draft'}
        open={editing !== null}
        onClose={() => setEditing(null)}
        width={720}
        extra={!isViewOnly && (
          <Button type="primary" loading={busy === 'save'} onClick={() => void save()}>Save draft</Button>
        )}
      >
        <Form form={form} layout="vertical" disabled={isViewOnly}>
          <Space wrap>
            <Form.Item name="country_code" label="Country" rules={[{ required: true }]}>
              <Input style={{ width: 90 }} maxLength={2} disabled={editing !== 'new'} />
            </Form.Item>
            <Form.Item name="export_type" label="Export type" rules={[{ required: true }]}>
              <Select style={{ width: 200 }} options={EXPORT_TYPES.map((t) => ({ value: t }))} disabled={editing !== 'new'} />
            </Form.Item>
            <Form.Item name="bank_code" label="Bank (bank files only)">
              <Input style={{ width: 110 }} placeholder="CRDB" disabled={editing !== 'new'} />
            </Form.Item>
            <Form.Item name="template_version" label="Version" rules={[{ required: true }]}>
              <Input style={{ width: 110 }} placeholder="2026.2" />
            </Form.Item>
            <Form.Item name="effective_from" label="Effective from">
              <Input style={{ width: 140 }} placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Space>
          <Form.Item name="spec" label="Spec (JSON — same schema as the country-pack export YAML)" rules={[{ required: true }]}>
            <Input.TextArea rows={22} style={{ fontFamily: 'monospace', fontSize: 12 }} spellCheck={false} />
          </Form.Item>
        </Form>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          The spec is validated with the exact parser generation uses before it saves. Publishing needs a
          different platform admin than the draft&apos;s author. Generated files record the version that
          produced them, so history stays attributable after every change.
        </Paragraph>
      </Drawer>
    </div>
  );
};

export default ExportTemplatesPage;
